/**
 * Lux New Arrival Tabs
 * - Lazy Swiper init per tab (first paint stays cheap).
 * - Mobile: 2 products per view, advancing 1 per swipe (slidesPerGroup: 1).
 * - Active-slide-only video playback; all videos pause on tab switch,
 *   when the section leaves the viewport, and for reduced-motion users.
 * - Full tab keyboard navigation (Left/Right/Home/End per WAI-ARIA).
 * - Graceful fallback to a native scroll rail if Swiper fails to load.
 * - Re-initializes cleanly on Theme Editor section reloads.
 */
(function () {
  'use strict';

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initSection(root) {
    if (!root || root.dataset.luxArrivalsInit) return;
    root.dataset.luxArrivalsInit = 'true';

    var tabs = Array.from(root.querySelectorAll('[data-arrivals-tab]'));
    var panels = Array.from(root.querySelectorAll('[data-arrivals-panel]'));
    var swipers = {};
    var swiperAvailable = typeof window.Swiper !== 'undefined';

    /* ---------- Swiper ---------- */

    function createSwiper(panel) {
      if (!panel || !swiperAvailable) return null;
      var index = panel.dataset.arrivalsPanel;
      if (swipers[index]) return swipers[index];

      var el = panel.querySelector('[data-arrivals-swiper]');
      if (!el) return null;

      var slideCount = el.querySelectorAll('.swiper-slide').length;
      var canLoop = slideCount >= 6; // safe margin above the 5-per-view max

      swipers[index] = new window.Swiper(el, {
        /* Mobile base: 2 side by side, one product per swipe */
        slidesPerView: 2,
        slidesPerGroup: 1,
        spaceBetween: 12,
        loop: canLoop,
        speed: 700,
        grabCursor: true,
        watchSlidesProgress: true,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: el.querySelector('.swiper-button-next'),
          prevEl: el.querySelector('.swiper-button-prev')
        },
        scrollbar: {
          el: panel.querySelector('.swiper-scrollbar'),
          draggable: true,
          hide: false,
          dragSize: 60
        },
        breakpoints: {
          768: { slidesPerView: 3, slidesPerGroup: 1, spaceBetween: 18 },
          1024: { slidesPerView: 4, slidesPerGroup: 1, spaceBetween: 20 },
          1400: { slidesPerView: 5, slidesPerGroup: 1, spaceBetween: 24 }
        },
        on: {
          init: function (sw) {
            panel.classList.add('swiper-ready');
            refreshActiveVideo(sw);
          },
          slideChange: function (sw) { refreshActiveVideo(sw); },
          slideChangeTransitionEnd: function (sw) { refreshActiveVideo(sw); }
        }
      });

      return swipers[index];
    }

    /* ---------- Video control ---------- */

    function refreshActiveVideo(swiper) {
      if (REDUCED_MOTION) return; // never autoplay for reduced-motion users
      swiper.slides.forEach(function (slide) {
        var video = slide.querySelector('[data-arrivals-video]');
        if (!video) return;
        if (
          slide.classList.contains('swiper-slide-active') ||
          slide.classList.contains('swiper-slide-next')
        ) {
          var attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(function () {});
        } else {
          video.pause();
        }
      });
    }

    function pauseAllVideos(exceptIndex) {
      panels.forEach(function (panel) {
        if (String(exceptIndex) === panel.dataset.arrivalsPanel) return;
        panel.querySelectorAll('[data-arrivals-video]').forEach(function (v) {
          v.pause();
        });
      });
    }

    /* ---------- Tabs (WAI-ARIA pattern) ---------- */

    function activateTab(index, focusTab) {
      tabs.forEach(function (tab) {
        var isActive = tab.dataset.arrivalsTab === String(index);
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
        if (isActive) {
          if (focusTab) tab.focus();
          /* Keep the active tab visible in the mobile scroll strip */
          if (typeof tab.scrollIntoView === 'function') {
            tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: REDUCED_MOTION ? 'auto' : 'smooth' });
          }
        }
      });

      panels.forEach(function (panel) {
        var isActive = panel.dataset.arrivalsPanel === String(index);
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      pauseAllVideos(index);

      var activePanel = panels.find(function (p) {
        return p.dataset.arrivalsPanel === String(index);
      });
      var swiper = createSwiper(activePanel);

      if (swiper) {
        requestAnimationFrame(function () {
          swiper.update();
          refreshActiveVideo(swiper);
        });
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        activateTab(tab.dataset.arrivalsTab, false);
      });

      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        activateTab(tabs[next].dataset.arrivalsTab, true);
      });
    });

    /* ---------- Viewport: pause everything off-screen ---------- */

    if ('IntersectionObserver' in window) {
      var visObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            pauseAllVideos(-1);
          } else {
            var active = root.querySelector('.lux-arrivals__panel.is-active');
            var index = active ? active.dataset.arrivalsPanel : null;
            if (index !== null && swipers[index]) refreshActiveVideo(swipers[index]);
          }
        });
      }, { threshold: 0.05 });
      visObserver.observe(root);
    }

    /* ---------- Init first panel ---------- */

    var firstActive = root.querySelector('.lux-arrivals__panel.is-active');
    if (swiperAvailable) {
      createSwiper(firstActive);
    }
    // If Swiper never loaded, panels keep the CSS scroll-rail fallback.
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-lux-arrivals]').forEach(initSection);
  }

  initAll();

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();